import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { axiosInstance as axios } from '../../context/AuthContext';
import { FaUserPlus, FaBook, FaExchangeAlt, FaCog, FaQuestionCircle, FaUserTie, FaEnvelope } from 'react-icons/fa';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

// Import refactored components
import {
  MemberCard,
  ProxyMemberCard,
  PendingApprovalsSection,
  AddMemberModal,
  AddProxyModal,
  TransferOwnershipModal,
  InviteProxyModal,
  EditProxyModal,
  InvitationSettingsModal,
  SentInvitationsModal,
  RemoveMemberDialog
} from './members';
import InviteProfessionalModal from '../Professionals/InviteProfessionalModal';

const API = '/api';

export function MembersTab({ members, proxyMembers, familyId, family, currentUser, showForm, setShowForm, onMemberAdded, hasPermission, isOwner }) {
  const [email, setEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [showProxyForm, setShowProxyForm] = useState(false);
  const [proxyName, setProxyName] = useState('');
  const [proxyRelationship, setProxyRelationship] = useState('');
  const [proxyEmail, setProxyEmail] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferToUserId, setTransferToUserId] = useState('');
  const [transferring, setTransferring] = useState(false);
  
  // Invitation state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteProxy, setInviteProxy] = useState(null);
  const [inviteMessage, setInviteMessage] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  
  // Edit proxy state
  const [showEditProxyModal, setShowEditProxyModal] = useState(false);
  const [editingProxy, setEditingProxy] = useState(null);
  const [editProxyForm, setEditProxyForm] = useState({ name: '', email: '', relationship: '' });
  const [savingProxy, setSavingProxy] = useState(false);
  
  // Invitation settings state
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [invitationSettings, setInvitationSettings] = useState({
    allow_all_members_to_invite: false,
    require_owner_approval: true
  });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Pending approvals state
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [processingApproval, setProcessingApproval] = useState(null);
  
  // Professional invitation state
  const [showInviteProfessionalModal, setShowInviteProfessionalModal] = useState(false);

  // Sent invitations modal
  const [showSentInvitations, setShowSentInvitations] = useState(false);

  // Remove member
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  const handleRemoveMember = async () => {
    setRemoving(true);
    try {
      const res = await axios.delete(`${API}/families/${familyId}/members/${memberToRemove.user_id}`, { withCredentials: true });
      toast.success(res.data.message);
      setMemberToRemove(null);
      onMemberAdded();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    } finally {
      setRemoving(false);
    }
  };

  useEffect(() => {
    if (familyId) {
      fetchInvitationSettings();
      if (isOwner) fetchPendingApprovals();
    }
  }, [familyId, isOwner]);
  
  const fetchInvitationSettings = async () => {
    try {
      const res = await axios.get(`${API}/families/${familyId}/invitation-settings`, { withCredentials: true });
      setInvitationSettings(res.data);
    } catch (error) {
      console.error('Error fetching invitation settings:', error);
    }
  };
  
  const fetchPendingApprovals = async () => {
    try {
      const res = await axios.get(`${API}/families/${familyId}/pending-approvals`, { withCredentials: true });
      setPendingApprovals(res.data);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
    }
  };
  
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/families/${familyId}/invitation-settings`, invitationSettings, { withCredentials: true });
      toast.success('Invitation settings updated');
      setShowSettingsModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSavingSettings(false);
    }
  };
  
  const handleApprove = async (invitationId) => {
    setProcessingApproval(invitationId);
    try {
      const res = await axios.post(`${API}/invitations/${invitationId}/approve`, {}, { withCredentials: true });
      toast.success(res.data.message);
      setPendingApprovals(prev => prev.filter(p => p.invitation_id !== invitationId));
      onMemberAdded();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessingApproval(null);
    }
  };
  
  const handleReject = async (invitationId, personName) => {
    if (!window.confirm(`Are you sure you want to reject ${personName}'s request to join the family?`)) return;
    setProcessingApproval(invitationId);
    try {
      await axios.post(`${API}/invitations/${invitationId}/reject`, { reason: 'Rejected by owner' }, { withCredentials: true });
      toast.success('Request rejected');
      setPendingApprovals(prev => prev.filter(p => p.invitation_id !== invitationId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessingApproval(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await axios.post(`${API}/families/${familyId}/members`, { email }, { withCredentials: true, headers: { 'Content-Type': 'application/json' } });
      await axios.post(`${API}/onboarding/complete-step?step=add_members`, {}, { withCredentials: true }).catch(() => {});
      alert('Member added successfully!');
      setShowForm(false);
      setEmail('');
      onMemberAdded();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  };

  const handleAddProxy = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await axios.post(`${API}/proxy-members`, { family_id: familyId, name: proxyName, relationship: proxyRelationship || null, email: proxyEmail || null }, { withCredentials: true });
      alert(`${proxyName} has been added to the family!`);
      setShowProxyForm(false);
      setProxyName('');
      setProxyRelationship('');
      setProxyEmail('');
      onMemberAdded();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  };

  const handleTransferOwnership = async () => {
    if (!transferToUserId) {
      toast.error('Please select a member to transfer ownership to');
      return;
    }
    if (!window.confirm('Are you sure you want to transfer ownership? This action cannot be undone.')) return;
    
    setTransferring(true);
    try {
      const res = await axios.post(`${API}/families/${familyId}/transfer-ownership?new_owner_id=${transferToUserId}`, {}, { withCredentials: true });
      toast.success(res.data.message);
      setShowTransferModal(false);
      setTransferToUserId('');
      onMemberAdded();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer ownership');
    } finally {
      setTransferring(false);
    }
  };

  const handleSendInvitation = async () => {
    if (!inviteProxy) return;
    setSendingInvite(true);
    try {
      const res = await axios.post(`${API}/invitations/send-to-proxy`, { proxy_id: inviteProxy.proxy_id, custom_message: inviteMessage || null }, { withCredentials: true });
      
      // Handle auto-conversion (proxy already had an account)
      if (res.data.auto_converted) {
        toast.success(res.data.message, { duration: 5000 });
        setShowInviteModal(false);
        setInviteProxy(null);
        setInviteMessage('');
        if (onMemberAdded) onMemberAdded();
        return;
      }
      
      const link = res.data.invitation_link;
      
      if (res.data.email_sent) {
        toast.success(`Invitation sent to ${inviteProxy.email}!`);
        // Also show the link in case email doesn't arrive (e.g., Yahoo)
        if (link) {
          try { await navigator.clipboard.writeText(link); } catch (e) {}
          toast.info('Link also copied to clipboard (in case email is delayed)', { duration: 4000 });
        }
      } else {
        if (link) {
          try { await navigator.clipboard.writeText(link); } catch (e) {}
          toast.success('Invitation link copied to clipboard!', { description: 'Share this link with the family member manually.', duration: 5000 });
          alert(`Email could not be sent automatically.\n\nPlease share this invitation link with ${inviteProxy.name}:\n\n${link}\n\n(Link has been copied to your clipboard)`);
        } else {
          toast.info(res.data.message);
        }
      }
      setShowInviteModal(false);
      setInviteProxy(null);
      setInviteMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSendingInvite(false);
    }
  };

  const openInviteModal = (proxy) => {
    if (!proxy.email) {
      toast.error('This member does not have an email address. Please add their email first.');
      return;
    }
    setInviteProxy(proxy);
    setShowInviteModal(true);
  };

  const openEditProxyModal = (proxy) => {
    setEditingProxy(proxy);
    setEditProxyForm({ name: proxy.name || '', email: proxy.email || '', relationship: proxy.relationship || '' });
    setShowEditProxyModal(true);
  };

  const handleSaveProxy = async () => {
    if (!editingProxy || !editProxyForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSavingProxy(true);
    try {
      const res = await axios.patch(`${API}/proxy-members/${editingProxy.proxy_id}`, {
        name: editProxyForm.name.trim(),
        email: editProxyForm.email.trim() || null,
        relationship: editProxyForm.relationship || null
      }, { withCredentials: true });
      toast.success(res.data.message);
      setShowEditProxyModal(false);
      setEditingProxy(null);
      onMemberAdded();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update member');
    } finally {
      setSavingProxy(false);
    }
  };

  return (
    <div data-testid="members-tab" className="space-y-6">
      {/* Banner */}
      <div className="rounded-xl overflow-hidden shadow-sm ring-1 ring-black/5" data-testid="members-banner">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/6e8c71c9-5deb-44de-959d-34c14877fd56/images/aee3cf167cd34c768c1152ee802b839465b1be55f26e07fa727afa5159358b6c.jpeg"
          alt="A multi-generational family gathered together at a backyard barbecue, smiling at the camera"
          className="w-full h-auto"
          loading="lazy"
        />
      </div>

      {/* Actions Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-800">Member Actions</h4>
          {isOwner && (
            <button onClick={() => setShowSettingsModal(true)} className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition" data-testid="invitation-settings-btn">
              <FaCog />
              <span>Invitation Settings</span>
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          {isOwner && (
            <button onClick={() => setShowForm(true)} className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold" data-testid="add-member-btn">
              <FaUserPlus />
              <span>Add Member (with account)</span>
            </button>
          )}
          <button onClick={() => setShowProxyForm(true)} className="flex items-center space-x-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold" data-testid="add-proxy-btn">
            <FaUserPlus />
            <span>Add Member (no account)</span>
          </button>
          <Link to="/role-guide" className="flex items-center space-x-2 px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 font-semibold" data-testid="role-guide-link">
            <FaBook />
            <span>Role Guide</span>
          </Link>
          {isOwner && (
            <button onClick={() => setShowTransferModal(true)} className="flex items-center space-x-2 px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-semibold" data-testid="transfer-ownership-btn">
              <FaExchangeAlt />
              <span>Transfer Ownership</span>
            </button>
          )}
          <button onClick={() => setShowInviteProfessionalModal(true)} className="flex items-center space-x-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold" data-testid="invite-professional-btn">
            <FaUserTie />
            <span>Invite a Professional</span>
          </button>
          <button onClick={() => setShowSentInvitations(true)} className="flex items-center space-x-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold" data-testid="sent-invitations-btn">
            <FaEnvelope />
            <span>Sent Invitations</span>
          </button>
        </div>
      </div>

      {/* Pending Approvals Section (Owner Only) */}
      {isOwner && (
        <PendingApprovalsSection
          pendingApprovals={pendingApprovals}
          processingApproval={processingApproval}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      )}

      {/* Account Members Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <h4 className="text-lg font-semibold text-gray-800">Account Members</h4>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/role-guide" className="text-gray-400 hover:text-purple-600 transition">
                  <FaQuestionCircle className="text-lg" />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs bg-slate-800 text-white p-3">
                <p className="font-medium mb-1">Assign roles to members!</p>
                <p className="text-sm text-gray-300">Go to the Roles tab to assign leadership positions like Vision Keeper, Scribe, or Harvest Guardian.</p>
                <p className="text-xs text-purple-300 mt-2">Click to learn more</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map((member) => (
            <MemberCard
              key={member.user_id}
              member={member}
              isMemberOwner={family?.created_by === member.user_id}
              onRemove={isOwner && member.user_id !== currentUser?.user_id ? setMemberToRemove : undefined}
            />
          ))}
        </div>
      </div>

      {/* Proxy Members Section */}
      {proxyMembers.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-800">Family Members (no account)</h4>
            <p className="text-sm text-gray-500">Invite them to create a free account!</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {proxyMembers.map((proxy) => (
              <ProxyMemberCard
                key={proxy.proxy_id}
                proxy={proxy}
                onEdit={openEditProxyModal}
                onInvite={openInviteModal}
              />
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      <AddMemberModal show={showForm} email={email} setEmail={setEmail} adding={adding} onSubmit={handleSubmit} onClose={() => { setShowForm(false); setEmail(''); }} />
      <AddProxyModal show={showProxyForm} proxyName={proxyName} setProxyName={setProxyName} proxyEmail={proxyEmail} setProxyEmail={setProxyEmail} proxyRelationship={proxyRelationship} setProxyRelationship={setProxyRelationship} adding={adding} onSubmit={handleAddProxy} onClose={() => { setShowProxyForm(false); setProxyName(''); setProxyRelationship(''); setProxyEmail(''); }} />
      <TransferOwnershipModal show={showTransferModal} members={members} currentUser={currentUser} transferToUserId={transferToUserId} setTransferToUserId={setTransferToUserId} transferring={transferring} onTransfer={handleTransferOwnership} onClose={() => { setShowTransferModal(false); setTransferToUserId(''); }} />
      <InviteProxyModal show={showInviteModal} inviteProxy={inviteProxy} inviteMessage={inviteMessage} setInviteMessage={setInviteMessage} sendingInvite={sendingInvite} onSend={handleSendInvitation} onClose={() => { setShowInviteModal(false); setInviteProxy(null); setInviteMessage(''); }} />
      <EditProxyModal show={showEditProxyModal} editingProxy={editingProxy} editProxyForm={editProxyForm} setEditProxyForm={setEditProxyForm} savingProxy={savingProxy} onSave={handleSaveProxy} onClose={() => { setShowEditProxyModal(false); setEditingProxy(null); }} />
      <InvitationSettingsModal show={showSettingsModal} invitationSettings={invitationSettings} setInvitationSettings={setInvitationSettings} savingSettings={savingSettings} onSave={handleSaveSettings} onClose={() => { setShowSettingsModal(false); fetchInvitationSettings(); }} />
      <SentInvitationsModal isOpen={showSentInvitations} onClose={() => setShowSentInvitations(false)} familyId={familyId} />
      <RemoveMemberDialog member={memberToRemove} removing={removing} onConfirm={handleRemoveMember} onClose={() => setMemberToRemove(null)} />
      
      {/* Invite Professional Modal */}
      {showInviteProfessionalModal && (
        <InviteProfessionalModal
          familyId={familyId}
          onClose={() => setShowInviteProfessionalModal(false)}
          onSuccess={() => {
            toast.success('Professional invitation created!');
          }}
        />
      )}
    </div>
  );
}
